import { uploadJobPDF, savePDFUrlToJob } from '../services/pdf-storage';
import jsPDF from 'jspdf';

// ═══════════════════════════════════════════════════════════════════════════════
// THE WINDOW PLACE LLC — CUT LIST PDF
// Layout: Full-width header → 2-column sections → Full-width summary bars
// Left col:  Frame, Sash
// Right col: Spring Balance (DH), Reinforcement, Glazbead, Mull Posts, J-Channel, Misc
//
// One window = one page, ALWAYS. When the content is too big to fit at normal
// dimensions, the layout automatically switches to a "compact" preset — tighter
// row heights, smaller gaps, slightly smaller fonts — so everything still fits
// on a single page. We never overflow to a second sheet.
// ═══════════════════════════════════════════════════════════════════════════════

const BROWN      = [101,  47,   8];
const BROWN_MID  = [160, 100,  45];
const BROWN_PALE = [245, 235, 220];
const WHITE      = [255, 255, 255];
const OFF_WHITE  = [252, 250, 247];
const L_GRAY     = [220, 220, 220];
const M_GRAY     = [150, 150, 150];
const DARK       = [ 28,  28,  28];

const TYPE_CFG = {
  DH:    { bg: [120,  50,  10], name: 'Double Hung'       },
  HS:    { bg: [ 20,  70, 140], name: 'Horizontal Slider' },
  '3HS': { bg: [  0,  90,  55], name: '3-Lite H. Slider'  },
  '3LHS':{ bg: [  0,  90,  55], name: '3-Lite H. Slider'  },
  PW:    { bg: [ 10, 100,  45], name: 'Picture Window'    },
  BSMT:  { bg: [ 70,  40, 110], name: 'Basement Hopper'   },
  CSMT:  { bg: [140,  65,   0], name: 'Casement'          },
  CS:    { bg: [140,  65,   0], name: 'Casement'          },
  AW:    { bg: [  0,  95,  95], name: 'Awning'            },
  FX:    { bg: [ 55,  55,  55], name: 'Fixed'             },
  BM:    { bg: [ 85,  45, 125], name: 'Bay / Mullion'     },
};
const tc = t => TYPE_CFG[t] ?? { bg: [75, 75, 75], name: t || 'Window' };

const SC = {
  springBalance: { bg: [254, 178, 178], hd: [127,  29,  29] },  // coral (distinct from frame)
  frame:         { bg: [134, 211, 156], hd: [ 10,  75,  30] },  // deep green
  sash:          { bg: [253, 211,  77], hd: [110,  70,  10] },  // gold
  glass:         { bg: [180, 215, 245], hd: [ 12,  60, 130] },  // sky blue — NEW
  screen:        { bg: [ 94, 192, 184], hd: [ 12,  74,  70] },  // deep teal — NEW
  reinforcement: { bg: [251, 178, 100], hd: [115,  45,  10] },  // orange
  mullPosts:     { bg: [196, 181, 253], hd: [ 80,  30, 145] },  // lavender
  jChannel:      { bg: [165, 220, 220], hd: [  5,  95,  95] },  // teal
  glazbead:      { bg: [249, 168, 212], hd: [130,  25,  70] },  // pink
  misc:          { bg: [203, 213, 225], hd: [ 35,  45,  65] },  // slate
};

const PAGE_W      = 216;
const PAGE_H      = 279;
const MARGIN      = 10;
const FTR_H       = 12;
const CONTENT_W   = PAGE_W - MARGIN * 2;
const SAFE_BOTTOM = PAGE_H - FTR_H - 4;
const COL_GAP     = 5;
const COL_W       = (CONTENT_W - COL_GAP) / 2;

// ─── DIMENSION PRESETS ───────────────────────────────────────────────────────
// NORM is the original, most readable layout. COMPACT shrinks heights, gaps,
// and font sizes so the same content fits in roughly two-thirds the vertical
// space. ULTRA is the emergency fallback for very heavy windows. The renderer
// picks the loosest preset whose estimated total height fits inside the
// available page space, so simple cases still look spacious and heavy cases
// still fit on one page.
const NORM_DIMS = {
  typeBannerH: 16, typeBannerGap: 4,
  titleBarH: 13, titleBarGap: 3,
  infoRowH: 9, gapAfterInfo: 5,
  notesPadTop: 5, notesPadBot: 4, notesLineH: 4, gapAfterNotes: 4, notesMinH: 10,
  secHdrH: 9, secRowH: 10, gapBetweenSections: 4,
  gapAfterSections: 5,
  summaryBarH: 10, gapBetweenSummaryBars: 2.5,
  fontTitleBar: 13,
  fontInfoLabel: 10, fontInfoValue: 10,
  fontNotesLabel: 9, fontNotesText: 9,
  fontSecHdr: 9, fontSecUnit: 7.5, fontSecRow: 11,
  fontSummaryLabel: 8, fontSummarySize: 13,
  fontQtyPill: 11,
};

const COMPACT_DIMS = {
  typeBannerH: 14, typeBannerGap: 2,
  titleBarH: 12, titleBarGap: 2,
  infoRowH: 8, gapAfterInfo: 3,
  notesPadTop: 3, notesPadBot: 2.5, notesLineH: 4, gapAfterNotes: 2, notesMinH: 8,
  secHdrH: 8, secRowH: 8, gapBetweenSections: 2,
  gapAfterSections: 3,
  summaryBarH: 9, gapBetweenSummaryBars: 1.5,
  fontTitleBar: 12,
  fontInfoLabel: 9, fontInfoValue: 9,
  fontNotesLabel: 8.5, fontNotesText: 8.5,
  fontSecHdr: 8.5, fontSecUnit: 7, fontSecRow: 10,
  fontSummaryLabel: 7.5, fontSummarySize: 12,
  fontQtyPill: 10,
};

const ULTRA_DIMS = {
  typeBannerH: 12, typeBannerGap: 1.5,
  titleBarH: 10, titleBarGap: 1.5,
  infoRowH: 6.5, gapAfterInfo: 2,
  notesPadTop: 2, notesPadBot: 2, notesLineH: 3.5, gapAfterNotes: 1.5, notesMinH: 6.5,
  secHdrH: 7, secRowH: 6.8, gapBetweenSections: 1.5,
  gapAfterSections: 2,
  summaryBarH: 8, gapBetweenSummaryBars: 1,
  fontTitleBar: 11,
  fontInfoLabel: 8.5, fontInfoValue: 8.5,
  fontNotesLabel: 8, fontNotesText: 8,
  fontSecHdr: 8, fontSecUnit: 6.5, fontSecRow: 9,
  fontSummaryLabel: 7, fontSummarySize: 11,
  fontQtyPill: 9,
};

// ── Number helpers ────────────────────────────────────────────────────────────
function num(v) {
  if (v == null || v === '') return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return String(v);
  return n.toFixed(4).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}
const dim = v => { const s = num(v); return s === '—' ? '—' : `${s}"`; };

// ═══════════════════════════════════════════════════════════════════════════════
// FOOTER
// ═══════════════════════════════════════════════════════════════════════════════
function drawFooter(doc, pg, total) {
  const fy = PAGE_H - FTR_H;
  doc.setFillColor(...WHITE); doc.rect(0, fy, PAGE_W, FTR_H, 'F');
  doc.setDrawColor(...BROWN_MID); doc.setLineWidth(0.5); doc.line(0, fy, PAGE_W, fy);
  doc.setFillColor(...BROWN); doc.rect(0, fy, 5, FTR_H, 'F');
  doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(...BROWN);
  doc.text(
    'The Window Place LLC  •  1300 E 4th Ave, Milbank, SD 57252  •  (605) 432-1834  •  office@thewindowplacellc.com',
    PAGE_W / 2, fy + 5, { align: 'center' }
  );
  const ts = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
           + '  ' + new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  doc.setFontSize(6); doc.setTextColor(...M_GRAY);
  doc.text(`Printed: ${ts}`, PAGE_W - MARGIN, fy + 10, { align: 'right' });
  doc.text(`Page ${pg} of ${total}`, MARGIN + 5, fy + 10);
}

// ═══════════════════════════════════════════════════════════════════════════════
// HEIGHT ESTIMATOR — figures out how tall the rendered page would be for a
// given (r, dims) pair WITHOUT actually drawing anything. Used to pick the
// smallest preset whose output fits inside SAFE_BOTTOM.
// ═══════════════════════════════════════════════════════════════════════════════
function estimateContentHeight(r, dims, doc, contentW) {
  const isCSMT = r.type === 'CSMT' || r.type === 'CS';
  const isDH   = r.type === 'DH';
  const is3LHS = r.type === '3LHS' || r.type === '3HS';
  const isPW   = r.type === 'PW';
  const isEqualThirds = is3LHS && r.threeHSLayout === 'thirds';

  let h = 0;

  // type banner + title bar
  h += dims.typeBannerH + dims.typeBannerGap;
  h += dims.titleBarH + dims.titleBarGap;

  // info rows
  let numInfoRows = 2; // Config + Width
  if (isCSMT) {
    numInfoRows += 2; // Handing + Indiv W
    if (r.mullCenter) numInfoRows += 1;
  } else {
    numInfoRows += 1; // Fin Type
    const showIndivW = r.indivW != null && r.indivW !== (r.overallW ?? r.width) && (r.config ?? 'Single') !== 'Single';
    if (showIndivW) numInfoRows += 1;
    if (r.balance?.size != null) numInfoRows += 1;
  }
  if (is3LHS) numInfoRows += 1; // Sash Layout
  h += numInfoRows * dims.infoRowH;
  h += dims.gapAfterInfo;

  // notes bar — measure actual wrapped line count
  if (r.options) {
    doc.setFontSize(dims.fontNotesText);
    doc.setFont('helvetica', 'normal');
    const lines = doc.splitTextToSize(String(r.options), contentW - 30);
    const nh = Math.max(dims.notesMinH, dims.notesPadTop + lines.length * dims.notesLineH + dims.notesPadBot);
    h += nh + dims.gapAfterNotes;
  }

  // 2-column sections — total height is the taller of the two columns
  function colH(rows, present) {
    if (!present || !rows?.length) return 0;
    // Glass rows with grids carry a ~0.85-row annotation each — count in height estimate
    const extraGridH = rows.reduce((sum, row) => sum + (row.grids ? (row.gridColor ? 1.70 : 0.85) : 0), 0);
    return dims.secHdrH + (rows.length + extraGridH) * dims.secRowH + dims.gapBetweenSections;
  }
  const leftH = colH(r.frame, r.frame?.length > 0) + colH(r.sash, r.sash?.length > 0);
  // Approximate screen rows count for height estimation (flex=1, std=2-3)
  const screenRowCount =
    (r.flexScreen ? 1 : 0) +
    (r.stdScreen?.width != null ? 1 : 0) +
    (r.stdScreen?.height != null ? 1 : 0) +
    ((r.stdScreen?.withPullQty || r.stdScreen?.pullQty || r.stdScreen?.noPullQty) ? 1 : 0);

  const rightH =
    colH(r.glass,         r.glass?.length > 0) +
    (screenRowCount > 0 ? (dims.secHdrH + screenRowCount * dims.secRowH + dims.gapBetweenSections) : 0) +
    (isDH ? colH(r.springBalance, r.springBalance?.length > 0) : 0) +
    colH(r.reinforcement, r.reinforcement?.length > 0) +
    colH(r.glazbead,      r.glazbead?.length > 0) +
    colH(r.mullPosts,     r.mullPosts?.length > 0) +
    colH(r.jChannel,      r.jChannel?.length > 0) +
    colH(r.misc,          r.misc?.length > 0);
  h += Math.max(leftH, rightH);
  h += dims.gapAfterSections;

  // summary bars
  let nBars = 1; // FRAME SIZE always
  if (is3LHS) {
    if (isEqualThirds) {
      if (r.summary?.equalSashWeld != null || r.summary?.lockSashW != null) nBars += 1;
    } else {
      if (r.summary?.lockSashW != null) nBars += 1;
      if (r.summary?.keepSashW != null) nBars += 1;
    }
  } else if (!isPW) {
    const sashH = r.summary?.sashHeight ?? r.summary?.glassH ?? null;
    if (sashH != null) nBars += 1;
  }
  if (isCSMT && r.summary?.sashWeldH != null) nBars += 1;
  if (isCSMT && r.summary?.sashWeldW_C != null) nBars += 1;
  h += nBars * (dims.summaryBarH + dims.gapBetweenSummaryBars);

  return h;
}

// Pick the loosest preset that still fits in available space.
function chooseDims(r, doc, startY) {
  const available = SAFE_BOTTOM - startY;
  if (estimateContentHeight(r, NORM_DIMS,    doc, CONTENT_W) <= available) return NORM_DIMS;
  if (estimateContentHeight(r, COMPACT_DIMS, doc, CONTENT_W) <= available) return COMPACT_DIMS;
  return ULTRA_DIMS;
}

// ═══════════════════════════════════════════════════════════════════════════════
// DRAW ONE WINDOW PAGE
// ═══════════════════════════════════════════════════════════════════════════════
function drawCutListPage(doc, r, job, startY) {
  const isCSMT     = r.type === 'CSMT' || r.type === 'CS';
  const isDH       = r.type === 'DH';
  const is3LHS     = r.type === '3LHS' || r.type === '3HS';
  const isPW       = r.type === 'PW';
  const isEqualThirds = is3LHS && (r.threeHSLayout === 'thirds');
  const cfg        = tc(r.type);

  // Pick layout preset based on estimated content height
  const D = chooseDims(r, doc, startY);
  const isCompact = D !== NORM_DIMS;
  const isUltra   = D === ULTRA_DIMS;

  let y = startY;
  const x = MARGIN, w = CONTENT_W;

  // ── TYPE BANNER ──────────────────────────────────────────────────────────────
  const bh = D.typeBannerH;
  doc.setFillColor(...cfg.bg);
  doc.roundedRect(x, y, w, bh, 2, 2, 'F');
  const pillW = 30, pillH = bh - 4;
  doc.setFillColor(
    Math.min(cfg.bg[0] + 55, 255),
    Math.min(cfg.bg[1] + 55, 255),
    Math.min(cfg.bg[2] + 55, 255)
  );
  doc.roundedRect(x + 3, y + 2, pillW, pillH, 1.5, 1.5, 'F');
  const codeFont = isUltra ? 9.5 : (isCompact ? 10 : 11);
  const nameFont = isUltra ? 12  : (isCompact ? 12.5 : 14);
  const subFont  = isUltra ? 8   : (isCompact ? 8.5 : 9);
  doc.setFontSize(codeFont); doc.setFont('helvetica', 'bold'); doc.setTextColor(...WHITE);
  doc.text(r.type || '—', x + 3 + pillW / 2, y + bh * 0.72, { align: 'center' });
  doc.setFontSize(nameFont);
  doc.text(cfg.name, x + pillW + 10, y + bh * 0.72);
  doc.setFontSize(subFont); doc.setFont('helvetica', 'normal'); doc.setTextColor(255, 240, 205);
  doc.text(
    `${job.customerName || 'Customer'}  •  WO# ${job.woNumber || '—'}`,
    x + w - 3, y + bh * 0.72, { align: 'right' }
  );
  y += bh + D.typeBannerGap;

  // ── TITLE BAR (label + qty) ──────────────────────────────────────────────────
  const th = D.titleBarH;
  doc.setFillColor(...BROWN);
  doc.roundedRect(x, y, w, th, 2, 2, 'F');
  doc.setFontSize(D.fontTitleBar); doc.setFont('helvetica', 'bold'); doc.setTextColor(...WHITE);
  doc.text(
    [r.label, r.type, r.color].filter(Boolean).join('  —  '),
    x + 5, y + th * 0.72, { maxWidth: w - 34 }
  );

  // Equal Thirds pill
  if (is3LHS && isEqualThirds) {
    const pillTxt = 'EQUAL THIRDS';
    doc.setFontSize(isUltra ? 7 : 8); doc.setFont('helvetica', 'bold');
    const pillTxtW = doc.getTextWidth(pillTxt);
    const pillPadW = 6;
    const pillW2   = pillTxtW + pillPadW * 2;
    const pillH2   = Math.min(8, th - 4);
    const pillX    = x + w - 32 - pillW2 - 4;
    const pillY    = y + (th - pillH2) / 2;
    doc.setFillColor(0, 170, 100);
    doc.roundedRect(pillX, pillY, pillW2, pillH2, 2, 2, 'F');
    doc.setTextColor(...WHITE);
    doc.text(pillTxt, pillX + pillPadW, pillY + pillH2 - 2.2);
  }

  doc.setFillColor(...WHITE);
  doc.roundedRect(x + w - 30, y + 2, 27, th - 4, 1.5, 1.5, 'F');
  doc.setFontSize(D.fontQtyPill); doc.setFont('helvetica', 'bold'); doc.setTextColor(...BROWN);
  doc.text(`Qty: ${r.qty ?? 1}`, x + w - 16.5, y + th * 0.72, { align: 'center' });
  y += th + D.titleBarGap;

  // ── INFO ROWS ─────────────────────────────────────────────────────────────────
  const C1 = x + 5, C2 = x + w * 0.28, C3 = x + w * 0.56, C4 = x + w * 0.78;
  function infoRow(l1, v1, l2, v2, shade) {
    const ih = D.infoRowH;
    doc.setFillColor(...(shade ? BROWN_PALE : WHITE));
    doc.rect(x, y, w, ih, 'F');
    doc.setDrawColor(...L_GRAY); doc.setLineWidth(0.2);
    doc.line(x, y + ih, x + w, y + ih);
    const ty = y + ih - 2.5;
    doc.setFontSize(D.fontInfoLabel); doc.setFont('helvetica', 'bold'); doc.setTextColor(...BROWN_MID);
    doc.text(String(l1), C1, ty);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(...DARK);
    doc.text(String(v1 ?? '—'), C2, ty);
    if (l2) {
      doc.setFont('helvetica', 'bold'); doc.setTextColor(...BROWN_MID);
      doc.text(String(l2), C3, ty);
      doc.setFont('helvetica', 'normal'); doc.setTextColor(...DARK);
      doc.text(String(v2 ?? '—'), C4, ty);
    }
    y += ih;
  }

  infoRow('Config',   r.config || 'Single',        'Qty',      r.qty ?? 1,                          false);
  infoRow('Width',    dim(r.overallW ?? r.width),  'Height',   dim(r.height),                       true);

  if (isCSMT) {
    infoRow('Handing',  r.handing || '—',          'Fin Type', r.finType || '—',                    false);
    infoRow('Indiv W',  dim(r.indivW),             'Notch',    r.notch || '—',                      true);
    if (r.mullCenter) infoRow('Ctr of Mull', dim(r.mullCenter), '', '', false);
  } else {
    infoRow('Fin Type', r.finType || '—',          'Notch',    r.balance?.notch || r.notch || '—',  true);
    const showIndivW = r.indivW != null
      && r.indivW !== (r.overallW ?? r.width)
      && (r.config ?? 'Single') !== 'Single';
    if (showIndivW) infoRow('Indiv W', dim(r.indivW), '', '', false);
    if (r.balance?.size != null) infoRow('Balance Size', dim(r.balance.size), '', '', true);
  }

  if (is3LHS) {
    infoRow(
      'Sash Layout',
      isEqualThirds ? 'Equal Thirds' : 'Quarter / Half / Quarter',
      '', '', false
    );
  }

  y += D.gapAfterInfo;

  // ── NOTES BAR (dynamic height — grows with wrapped line count) ───────────────
  if (r.options) {
    const optText = String(r.options);
    doc.setFontSize(D.fontNotesText); doc.setFont('helvetica', 'normal');
    const lines = doc.splitTextToSize(optText, w - 30);
    const nh = Math.max(D.notesMinH, D.notesPadTop + lines.length * D.notesLineH + D.notesPadBot);

    doc.setFillColor(253, 245, 235); doc.rect(x, y, w, nh, 'F');
    doc.setFillColor(...BROWN); doc.rect(x, y, 3, nh, 'F');
    doc.setDrawColor(...BROWN_MID); doc.setLineWidth(0.3);
    doc.line(x, y + nh, x + w, y + nh);

    doc.setFontSize(D.fontNotesLabel); doc.setFont('helvetica', 'bold'); doc.setTextColor(...BROWN_MID);
    doc.text('NOTES:', x + 6, y + D.notesPadTop + 1);
    doc.setFontSize(D.fontNotesText); doc.setFont('helvetica', 'normal'); doc.setTextColor(...DARK);
    doc.text(lines, x + 26, y + D.notesPadTop + 1);

    y += nh + D.gapAfterNotes;
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // 2-COLUMN SECTION AREA
  // ═════════════════════════════════════════════════════════════════════════════
  const LEFT_X        = MARGIN;
  const RIGHT_X       = MARGIN + COL_W + COL_GAP;
  const sectionStartY = y;

  const SEC_HDR_H = D.secHdrH;
  const SEC_ROW_H = D.secRowH;
  const QTY_RIGHT = COL_W * 0.74;
  const LEN_RIGHT = COL_W - 2;

  function drawColSection(colX, colY, key, rows, label, isSpring) {
    if (!rows?.length) return colY;
    const sc = SC[key] ?? SC.misc;

    doc.setFillColor(...sc.bg);
    doc.rect(colX, colY, COL_W, SEC_HDR_H, 'F');
    doc.setFontSize(D.fontSecHdr); doc.setFont('helvetica', 'bold'); doc.setTextColor(...sc.hd);
    doc.text(label, colX + 4, colY + SEC_HDR_H - 2.2);
    doc.setFontSize(D.fontSecUnit); doc.setTextColor(...sc.hd);
    const isWxHHeader = key === 'glass' || key === 'screen';
    if (!isSpring && !isWxHHeader) {
      doc.text('QTY',    colX + QTY_RIGHT,  colY + SEC_HDR_H - 2.2, { align: 'right' });
    }
    doc.text(isWxHHeader ? 'W × H' : 'LENGTH', colX + LEN_RIGHT, colY + SEC_HDR_H - 2.2, { align: 'right' });
    colY += SEC_HDR_H;

    rows.forEach((row, i) => {
      doc.setFillColor(...(i % 2 === 0 ? WHITE : OFF_WHITE));
      doc.rect(colX, colY, COL_W, SEC_ROW_H, 'F');
      doc.setDrawColor(...L_GRAY); doc.setLineWidth(0.18);
      doc.line(colX, colY + SEC_ROW_H, colX + COL_W, colY + SEC_ROW_H);

      const rowLbl = (row.label || '—').replace(/\s*\([^)]*\)/g, '').trim();
      const ty = colY + SEC_ROW_H - 2.2;

      doc.setFontSize(D.fontSecRow); doc.setFont('helvetica', 'normal'); doc.setTextColor(...DARK);
      doc.text(rowLbl, colX + 4, ty, { maxWidth: COL_W * 0.57 });

      const isWxHRow = row.width != null && row.height != null;
      // Skip QTY column for glass rows — qty embedded in value text below to prevent column collision
      if (!isSpring && !isWxHRow) {
        doc.setFont('helvetica', 'bold');
        doc.text(String(row.qty ?? ''), colX + QTY_RIGHT, ty, { align: 'right' });
      }

      doc.setFont('helvetica', 'normal');
      if (isWxHRow) {
        // Glass: render "(×qty)  W × H" right-aligned, takes full clearance now that QTY column is gone
        const qtyPrefix = row.qty ? `(×${row.qty})  ` : '';
        doc.setFont('helvetica', 'bold');
        doc.text(
          `${qtyPrefix}${num(row.width)}" × ${num(row.height)}"`,
          colX + LEN_RIGHT, ty, { align: 'right' }
        );
      } else {
        doc.text(
          row.length != null ? dim(row.length) : '—',
          colX + LEN_RIGHT, ty, { align: 'right' }
        );
      }

      colY += SEC_ROW_H;

      // Glass rows with grids: compact annotation sub-row below
      // Attached to its parent row visually (no divider between row and annotation)
      // — uses tinted background matching the section color so it reads as a continuation
      if (key === 'glass' && row.grids) {
        const annotH = SEC_ROW_H * 0.85;
        // Tint matching section color but very soft — visually anchors to parent row
        doc.setFillColor(
          Math.round((sc.bg[0] * 0.35) + (255 * 0.65)),
          Math.round((sc.bg[1] * 0.35) + (255 * 0.65)),
          Math.round((sc.bg[2] * 0.35) + (255 * 0.65))
        );
        doc.rect(colX, colY, COL_W, annotH, 'F');
        doc.setDrawColor(...L_GRAY); doc.setLineWidth(0.18);
        doc.line(colX, colY + annotH, colX + COL_W, colY + annotH);

        // Build compact grid spec line (sparse — only present fields)
        const parts = [];
        if (row.gridType) {
          // Abbreviate "Contour Grids" → "Contour", "Flat Grids" → "Flat"
          parts.push(String(row.gridType).replace(/\s*Grids$/i, ''));
        }
        if (row.gridPattern) parts.push(row.gridPattern);
        if (row.gridQtyV != null && row.gridQtyH != null) {
          parts.push(`${row.gridQtyV}V × ${row.gridQtyH}H`);
        }
        const totalBars = (row.vertQty || 0) + (row.horizQty || 0);
        if (totalBars) parts.push(`${totalBars} bars`);

        const annotText = parts.join(' • ');

        // Slightly larger font, normal weight, dark text — readable
        doc.setFontSize(Math.max(D.fontSecRow - 1.4, 6.2));
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...sc.hd);
        // No maxWidth — let text render at natural width to avoid character spacing distortion
        doc.text(`Grids:  ${annotText}`, colX + 8, colY + annotH - 2.2);

        colY += annotH;

        // ── Second annot row: Inside Grid Color (only if present) ──
        // Rendered separately so long color names never overflow the card.
        if (row.gridColor) {
          doc.setFillColor(
            Math.round((sc.bg[0] * 0.25) + (255 * 0.75)),
            Math.round((sc.bg[1] * 0.25) + (255 * 0.75)),
            Math.round((sc.bg[2] * 0.25) + (255 * 0.75))
          );
          doc.rect(colX, colY, COL_W, annotH, 'F');
          doc.setDrawColor(...L_GRAY); doc.setLineWidth(0.18);
          doc.line(colX, colY + annotH, colX + COL_W, colY + annotH);
          doc.setFontSize(Math.max(D.fontSecRow - 1.4, 6.2));
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...sc.hd);
          doc.text(`Inside Grid Color:  ${row.gridColor}`, colX + 8, colY + annotH - 2.2);
          colY += annotH;
        }
      }
    });

    return colY + D.gapBetweenSections;
  }

  const miscRows = (r.misc || []);

  const leftSections = [
    { key: 'frame', rows: r.frame, label: 'FRAME', isSpring: false },
    { key: 'sash',  rows: r.sash,  label: 'SASH',  isSpring: false },
  ];

  // Normalize screen data — minimal display: just size variant + W×H + qty prefix
  // (Flex screen + STD mesh are defaults; only Half/Full Scrn distinction matters to the shop)
  const screenRows = (() => {
    const out = [];
    // Flex screen is the default for nearly every job — show as W×H row like glass
    if (r.flexScreen) {
      const f = r.flexScreen;
      const sizeLabel = r.screenSize || 'Full Scrn';
      out.push({
        label:     sizeLabel,    // 'Full Scrn' or 'Half Scrn'
        qty:       f.qty,
        width:     f.width,
        height:    f.height,
      });
    }
    // Standard screens (rare) — stay as conventional Widths/Heights cut rows
    if (r.stdScreen) {
      const s = r.stdScreen;
      const wQty = s.widthQty ?? s.wQty ?? s.qty ?? '';
      const hQty = s.heightQty ?? s.hQty ?? s.qty ?? '';
      if (s.width  != null) out.push({ label: 'Std Widths',  qty: wQty, length: s.width  });
      if (s.height != null) out.push({ label: 'Std Heights', qty: hQty, length: s.height });
      const pullParts = [];
      if (s.withPullQty || s.pullQty) pullParts.push(`${s.withPullQty ?? s.pullQty} w/ pull`);
      if (s.noPullQty)                pullParts.push(`${s.noPullQty} no pull`);
      if (pullParts.length) {
        out.push({ label: `Pulls:  ${pullParts.join('  ·  ')}`, qty: '', length: '' });
      }
    }
    return out;
  })();

  const rightSections = [
    { key: 'glass',         rows: r.glass,         label: 'GLASS',         isSpring: false },
    ...(screenRows.length ? [{ key: 'screen', rows: screenRows, label: 'SCREEN', isSpring: false }] : []),
    ...(isDH ? [{ key: 'springBalance', rows: r.springBalance, label: 'SPRING HEIGHT', isSpring: true }] : []),
    { key: 'reinforcement', rows: r.reinforcement, label: 'REINFORCEMENT', isSpring: false },
    { key: 'glazbead',      rows: r.glazbead,      label: 'GLAZBEAD',      isSpring: false },
    { key: 'mullPosts',     rows: r.mullPosts,     label: 'MULL POSTS',    isSpring: false },
    { key: 'jChannel',      rows: r.jChannel,      label: 'J-CHANNEL',     isSpring: false },
    { key: 'misc',          rows: miscRows,         label: 'MISC',          isSpring: false },
  ];

  let leftY  = sectionStartY;
  let rightY = sectionStartY;

  for (const sec of leftSections) {
    leftY = drawColSection(LEFT_X, leftY, sec.key, sec.rows, sec.label, sec.isSpring);
  }
  for (const sec of rightSections) {
    rightY = drawColSection(RIGHT_X, rightY, sec.key, sec.rows, sec.label, sec.isSpring);
  }

  const colBottom = Math.max(leftY, rightY) - 2;
  doc.setDrawColor(...L_GRAY); doc.setLineWidth(0.5);
  doc.line(MARGIN + COL_W + COL_GAP / 2, sectionStartY, MARGIN + COL_W + COL_GAP / 2, colBottom);

  y = colBottom + D.gapAfterSections;

  // ── SUMMARY BARS (full width) ─────────────────────────────────────────────────
  // No more silent skipping. Since we chose dimensions sized to fit the page,
  // every bar gets drawn on the same sheet.
  function summBar(bg, lbl, wid, ht) {
    const sbh = D.summaryBarH;
    doc.setFillColor(...bg); doc.roundedRect(x, y, w, sbh, 1.5, 1.5, 'F');
    doc.setFillColor(...BROWN); doc.roundedRect(x + 2, y + 1.5, 42, sbh - 3, 1, 1, 'F');
    doc.setFontSize(D.fontSummaryLabel); doc.setFont('helvetica', 'bold'); doc.setTextColor(...WHITE);
    doc.text(lbl, x + 23, y + sbh * 0.72, { align: 'center' });
    doc.setFontSize(D.fontSummarySize); doc.setFont('helvetica', 'bold'); doc.setTextColor(...BROWN);
    // Always width-first: "W … × H …" on every window type, sliders included.
    doc.text(`W  ${num(wid)}"   ×   H  ${num(ht)}"`, x + 48, y + sbh * 0.74);
    y += sbh + D.gapBetweenSummaryBars;
  }

  // Always show frame size
  summBar([232, 215, 195], 'FRAME SIZE', r.overallW ?? r.width, r.height);

  if (is3LHS) {
    if (isEqualThirds) {
      if (r.summary?.equalSashWeld != null) {
        summBar([200, 185, 160], 'EQUAL SASH', r.summary.equalSashWeld, r.summary.sashHeight);
      } else if (r.summary?.lockSashW != null) {
        const eqW = ((r.summary.lockSashW * 2) + r.summary.keepSashW) / 3;
        summBar([200, 185, 160], 'EQUAL SASH', eqW, r.summary.sashHeight);
      }
    } else {
      if (r.summary?.lockSashW != null)
        summBar([218, 200, 178], 'LOCK SASH',   r.summary.lockSashW,  r.summary.lockSashH ?? r.summary.sashHeight);
      if (r.summary?.keepSashW != null)
        summBar([200, 185, 160], 'KEEPER SASH', r.summary.keepSashW,  r.summary.keepSashH ?? r.summary.sashHeight);
    }
  } else if (isPW) {
    // Picture Window — no sash bar
  } else {
    const sashH = r.summary?.sashHeight ?? r.summary?.glassH ?? null;
    const sashW = r.summary?.sashWidth  ?? r.summary?.glassW ?? null;
    if (sashH != null) summBar([218, 200, 178], 'SASH SIZE', sashW, sashH);
  }

  // CSMT weld bars
  if (isCSMT && r.summary?.sashWeldH != null)
    summBar([202, 182, 158], 'SASH WELD', r.summary.sashWeldW, r.summary.sashWeldH);
  if (isCSMT && r.summary?.sashWeldW_C != null)
    summBar([185, 165, 140], 'CTR WELD', r.summary.sashWeldW_C, r.summary.sashWeldH_C);
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN EXPORT
// ═══════════════════════════════════════════════════════════════════════════════
// Collapse identical mulled panes into single sheets with summed qty
// (e.g. 2 identical DH panes from a mulled assembly => 1 sheet, qty 2)
function collapseIdenticalCutListWindows(windows) {
  if (!Array.isArray(windows) || windows.length === 0) return windows || [];
  const norm = v => (v === null || v === undefined) ? '' : String(v).trim().toLowerCase();
  const nf = v => { const n = parseFloat(v); return isNaN(n) ? '' : String(n); };
  const keyOf = w => [
    norm(w.type || w.code || w.series),
    nf(w.overallW || w.width),
    nf(w.height),
    norm(w.color || w.frameColor),
    norm(w.handing),
    norm(w.options),
    !!w.grids,
    norm(w.gridPattern),
    nf(w.gridQtyV),
    nf(w.gridQtyH),
    norm(w.gridColor),
    norm(w.screenType),
    norm(w.screenSize),
    norm(w.flanges),
  ].join('|');
  const isPW = w => norm(w.type || w.code || w.series).startsWith('pw') || norm(w.series).includes('picture');
  const groups = new Map();
  windows.forEach((w, i) => {
    const k = keyOf(w);
    const qty = parseFloat(w.qty) || 1;
    if (groups.has(k)) groups.get(k).qty += qty;
    else groups.set(k, { row: { ...w }, qty, firstIdx: i });
  });
  const collapsed = [...groups.values()]
    .sort((a, b) => a.firstIdx - b.firstIdx)
    .map(g => ({ ...g.row, qty: g.qty }));
  return collapsed
    .map((r, i) => ({ r, i, pw: isPW(r) ? 0 : 1 }))
    .sort((a, b) => a.pw - b.pw || a.i - b.i)
    .map(x => x.r);
}

export async function generateCutListPDF(job, calculatedWindows, returnBlob = false) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  // CUT LIST RULE: every window gets its own row (never merge).
  // Standalones can differ on tempered, notes, grids, or other specs not captured in any key.
  // Only PW-sorting is preserved from the old collapse function.
  calculatedWindows = (calculatedWindows || [])
    .map((r, i) => {
      const t = String(r.type || r.code || r.series || '').toLowerCase();
      const s = String(r.series || '').toLowerCase();
      const isPW = t.startsWith('pw') || s.includes('picture');
      return { r, i, pw: isPW ? 0 : 1 };
    })
    .sort((a, b) => a.pw - b.pw || a.i - b.i)
    .map(x => x.r);
  const totalPages = calculatedWindows.length;

  calculatedWindows.forEach((w, idx) => {
    if (idx > 0) doc.addPage();
    drawFooter(doc, idx + 1, totalPages);

    let contentStartY = 8;

    if (idx === 0 && job.notes) {
      const notesY = 5, notesH = 8;
      doc.setFillColor(253, 245, 235);
      doc.rect(0, notesY, PAGE_W, notesH, 'F');
      doc.setDrawColor(...BROWN_MID); doc.setLineWidth(0.4);
      doc.line(0, notesY + notesH, PAGE_W, notesH);
      doc.setFillColor(...BROWN); doc.rect(0, notesY, 2.5, notesH, 'F');
      doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(...BROWN_MID);
      doc.text('JOB NOTES:', 6, notesY + 5);
      doc.setFont('helvetica', 'normal'); doc.setTextColor(...DARK);
      doc.text(String(job.notes), 30, notesY + 5, { maxWidth: PAGE_W - 36 });
      contentStartY = notesY + notesH + 3;
    }

    drawCutListPage(doc, w, job, contentStartY);
  });

  const name = (job.customerName || 'Customer').replace(/\s+/g, '_');
  const wo   = job.woNumber || 'DRAFT';

  const _blob        = doc.output('blob');
  const _contractNum = (job.woNumber || 'DRAFT').replace(/\s+/g, '_');
  uploadJobPDF(_contractNum, 'cutlist', _blob)
    .then(url => savePDFUrlToJob(job.twpJobId || null, job.id || null, 'cutlist', url))
    .catch(e => console.warn('PDF upload skipped:', e));

  if (returnBlob) return doc.output('blob');
  doc.save(`${name}_WO${wo}_CutList.pdf`);
}
